import { useRouter } from "@tanstack/react-router";
import { ChevronDown, LogOut } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { signOut, useSession } from "@/lib/auth-client";
import { cn } from "@/lib/utils";

function initials(name?: string | null) {
  if (!name) return "U";
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase() || "U";
}

function roleAbbr(role?: string | null) {
  if (!role) return null;
  const r = role.toUpperCase();
  if (r === "ADMIN") return "Admin";
  if (["CEO", "RM", "SA", "ACCT", "FIN"].includes(r)) return r;
  return role;
}

export function UserMenu({ collapsed = false }: { collapsed?: boolean }) {
  const { data: session } = useSession();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const router = useRouter();

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  if (!session?.user) return null;

  const user = session.user;
  const role = roleAbbr(user.role);

  async function handleSignOut() {
    await signOut();
    router.navigate({ to: "/login" });
  }

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title={collapsed ? user.name : undefined}
        className={cn(
          "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-xs text-white/90 transition-colors hover:bg-white/10",
          collapsed && "justify-center px-0",
        )}
      >
        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-white/15 text-[10px] font-semibold uppercase">
          {initials(user.name)}
        </span>
        {!collapsed && (
          <>
            <span className="truncate text-left">
              {user.name}
              {role && <span className="text-white/60"> · {role}</span>}
            </span>
            <ChevronDown className="ml-auto h-3 w-3 shrink-0 text-white/60" />
          </>
        )}
      </button>

      {open && (
        <div
          className={cn(
            "absolute bottom-[calc(100%+6px)] z-50 overflow-hidden rounded-lg border border-border bg-card text-foreground shadow-lg",
            collapsed ? "left-0 w-60" : "left-0 right-0",
          )}
        >
          <div className="border-b border-border px-3 py-2.5">
            <div className="text-[13px] font-semibold leading-tight">{user.name}</div>
            <div className="text-[11px] text-muted-foreground">{user.email}</div>
            {role && (
              <div className="mt-0.5 text-[10px] text-muted-foreground">
                {role === "RM"
                  ? "Resource Manager"
                  : role === "SA"
                    ? "Solution Architect"
                    : role === "ACCT"
                      ? "Account Owner"
                      : role === "FIN"
                        ? "Finance / Ops"
                        : role === "CEO"
                          ? "Operator"
                          : role}
              </div>
            )}
          </div>
          <div className="p-1.5 text-[12px]">
            {[
              { label: "My Profile", aux: "—" },
              { label: "Notifications", aux: "3" },
              { label: "Preferences", aux: "" },
              { label: "Help & Docs", aux: "" },
            ].map((item) => (
              <div
                key={item.label}
                className="flex cursor-pointer items-center justify-between rounded px-3 py-1.5 hover:bg-muted"
              >
                <span>{item.label}</span>
                {item.aux && <span className="text-[11px] text-muted-foreground">{item.aux}</span>}
              </div>
            ))}
          </div>
          <div className="border-t border-border p-1.5">
            <button
              type="button"
              onClick={handleSignOut}
              className="flex w-full items-center gap-2 rounded px-3 py-1.5 text-[12px] font-semibold text-destructive hover:bg-muted"
            >
              <LogOut className="h-3.5 w-3.5" />
              Sign out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
